

































































import { defineComponent } from '@vue/composition-api';
import { mapMutations } from 'vuex';
import ERPRepository from '@/repositories/ERPRepository';
import AccountsLogRepository from '@/repositories/AccountsLogRepository';
import DialogHelper from '@/helpers/DialogHelper';
import InputRulesHelper from '@/helpers/InputRulesHelper';
import PredictedFlowHelper from '@/views/cashFlow/predictedFlow/helpers/PredictedFlowHelper';
import { VForm } from '@/types/VForm';
import CashFlowTablesEnum from '@/views/cashFlowGroupers/mainGrouper/enums/CashFlowTablesEnum';
import PredictedFlowActionsEnum from '@/views/cashFlow/predictedFlow/enums/PredictedFlowActionsEnum';
import ActionsWithoutReloadEnum from '@/views/cashFlow/predictedFlow/enums/ActionsWithoutReloadEnum';
import ERPEventsEnum from '@/domain/enums/ERPEventsEnum';
import AccountActionsEnum from '@/repositories/parameters/AccountLogs/enums/AccountActionsEnum';
import IChangeValueExternalERPRequestParams from '@/views/cashFlow/predictedFlow/interfaces/actions/IChangeValueExternalERPRequestParams';
import IPredictedFlowGrouperList from '@/views/cashFlow/predictedFlow/interfaces/groupers/IPredictedFlowGrouperList';
import IPredictedFlowGrouperItemData from '@/views/cashFlow/predictedFlow/interfaces/groupers/IPredictedFlowGrouperItemData';

export default defineComponent({
  name: 'ChangeValueDialog',
  props: {
    value: {
      required: true,
      type: Boolean,
    },
    companyGroupId: {
      required: true,
      type: Number,
    },
    register: {
      required: false,
      type: Object as () => IPredictedFlowGrouperList,
    },
    dateCorrespondentPeriodicity: {
      required: false,
      type: String,
    },
    itemIndex: {
      required: false,
      type: Number,
    },
    totalIndex: {
      required: false,
      type: Number,
    },
  },
  computed: {
    ...mapMutations(['triggerEventSend']),
    dialogWidth(): string {
      return DialogHelper.getDialogWidth(this.$vuetify.breakpoint, {
        smAndDown: '100%',
        md: '60%',
        default: '40%',
      });
    },
    tableName(): string {
      return PredictedFlowHelper.getTableBasedOnNcc(this.register!, Boolean(this.$session.get('use_ncc')));
    },
    itemData(): IPredictedFlowGrouperItemData {
      return this.register!.itemData!;
    },
    grouperType(): CashFlowTablesEnum {
      return this.register!.grouperType;
    },
  },
  watch: {
    value(): void {
      this.isOpen = this.value;
    },
  },
  data() {
    const inputRules: InputRulesHelper = new InputRulesHelper();
    const erpRepository: ERPRepository = new ERPRepository();
    const accountsLogRepository: AccountsLogRepository = new AccountsLogRepository();

    const financialValue: string = '';

    const loading: boolean = false;
    const isOpen: boolean = this.value;

    return {
      inputRules,
      erpRepository,
      accountsLogRepository,
      financialValue,
      loading,
      isOpen,
    };
  },
  methods: {
    async changeValue(): Promise<void> {
      try {
        this.loading = true;

        const requests = [
          this.updateInternalERPValue(),
        ];

        if (this.grouperType !== CashFlowTablesEnum.BANKS) {
          requests.unshift(this.updateExternalERPValue(), this.updateFieldInternal());
        }

        const response = await Promise.allSettled(requests);

        if (this.register!.grouperType !== CashFlowTablesEnum.BANKS) {
          await this.accountsLogRepository.createLog(
            this.companyGroupId,
            {
              type: this.tableName === 'account_receivable' ? 'receivable' : 'payable',
              company_id: this.itemData.companyId,
              action: AccountActionsEnum.CHANGE_VALUE,
              content: 'Alteração de valor feita com sucesso.',
              id_customer: this.itemData.idCustomer,
            },
          );
        }

        if (!response.every(({ status }) => status === 'fulfilled')) {
          throw new Error();
        }

        this.$notification.success('Valor alterado com sucesso!');

        this.$store.commit('predicted_flow/triggerEventSend', {
          action: ActionsWithoutReloadEnum.change_value,
          itemIndex: this.itemIndex,
          data: {
            value: Number(this.financialValue),
            totalIndex: this.totalIndex,
          },
        });

        this.close();
      } catch (error) {
        this.$notification.error('Houve um problema ao alterar o valor.');
      } finally {
        this.loading = false;
      }
    },
    async updateExternalERPValue(): Promise<void> {
      await this.erpRepository.updateExternalERP<IChangeValueExternalERPRequestParams>(
        this.companyGroupId,
        this.itemData.companyId,
        {
          id: this.itemData.idCustomer,
          event: ERPEventsEnum.INNCASH_VALUE,
          data: {
            inncash_value: Number(this.financialValue),
            type: this.tableName,
          },
        },
      );
    },
    async updateInternalERPValue(): Promise<void> {
      let definedDate: string = this.itemData.date;

      if (this.grouperType === CashFlowTablesEnum.BANKS) {
        definedDate = this.dateCorrespondentPeriodicity!;
      }

      await this.erpRepository.saveDataInternalTable({
        _identifyValue: PredictedFlowHelper.getTableAliasByGrouperType(this.grouperType),
        _field: 'valor',
        _date: definedDate,
        _keyId: this.itemData.keyId,
        _data: this.financialValue,
      });
    },
    async updateFieldInternal(): Promise<void> {
      const fieldBasedOnTable = this.grouperType === CashFlowTablesEnum.ACCOUNTS_RECEIVABLE ? 'e1_zvalinn' : 'e2_zvalinn';

      await this.erpRepository.updateInternalERP({
        id: this.itemData.id,
        field: fieldBasedOnTable,
        identify_values: PredictedFlowHelper.getTableAliasByGrouperType(this.grouperType),
        value: this.financialValue,
      });
    },
    close(): void {
      this.$emit('close', PredictedFlowActionsEnum.change_value);
    },
    handleChangeValue(): void {
      const changeValueForm = this.$refs.ChangeValueForm as VForm;

      if (!changeValueForm.validate()) {
        this.$notification.error('Há campos preenchidos incorretamente!');
        return;
      }

      this.changeValue();
    },
  },
});
